<template>
	<div class="new-table-view" :class="{ 'is-search-on': isSearchOn, 'is-multiple-selection': isMultipleSelection }">
		<div class="app-search-container" v-if="isSearchOn">
			<AppSearch :searchString.sync="searchString" placeholder="Search table" />
		</div>
		<AgGridVue
			class="ag-theme-alpine"
			:class="{ 'is-fields-editable': fieldsEditable }"
			:columnDefs="headers.map((h) => ({ ...h, ...{ editable: fieldsEditable } }))"
			:rowData="localRows"
			:statusBar="statusBar"
			:pagination="true"
			:paginationPageSize="localPageSize"
			v-bind="gridOptions"
			v-on="gridListeners"
			@grid-ready="onGridReady"
		>
		</AgGridVue>
		<TableStatusBar
			:gridApi="gridApi"
			:columnDefs="headers"
			:rows="localRows"
			:selectedRows="selectedRows"
			:pageSize.sync="localPageSize"
			:currentPage="currentPage"
			:totalPageNum="totalPageNum"
			@gotoPage="gotoPage"
			@reset="reset"
			@addRow="addRow"
			:addRowEnabled="addRowEnabled"
		/>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';

import { INTERNAL_ID_KEY } from '@ply-io/node-input-parser';

import EditableCellRenderer from './EditableCellRenderer';
import TableStatusBar from './TableStatusBar';
const AgGridVue = () => import(/* webpackPrefetch: true */ 'ag-grid-vue').then((result) => result.AgGridVue);

export default {
	name: 'NewTableView',
	components: {
		AgGridVue,
		TableStatusBar,
		// eslint-disable-next-line
		EditableCellRenderer,
	},
	props: {
		headers: { type: Array },
		rows: { type: Array },
		isSelectionOn: { type: Boolean },
		isMultipleSelection: { type: Boolean },
		isSearchOn: { type: Boolean },
		selectedRows: { type: [Array, Object] },
		isEditMode: { type: Boolean, default: false },
		fieldsEditable: { type: Boolean, default: true },
		addRowEnabled: { type: Boolean, default: false },
		pageSize: { type: Number, default: 5 },
	},
	methods: {
		onGridReady(params) {
			this.gridApi = params.api;
			this.gridColumnApi = params.columnApi;
			const exposedApi = {
				setSelectedRow: (rowIndex) => {
					params.api.forEachNode((node) => {
						if (node.rowIndex === rowIndex) {
							node.setSelected(true);
						}
					});
				},
			};
			this.$emit('onGridReady', exposedApi);
			setTimeout(() => {
				params.api.sizeColumnsToFit(); // trigger resize when editing table in the Builder
				this.setTotalPageNum();
			}, 100);
		},
		reset() {
			this.gridApi.deselectAll();
		},
		gotoPage(pageNum) {
			this.gridApi.paginationGoToPage(pageNum - 1);
			this.$set(this, 'currentPage', this.gridApi?.paginationGetCurrentPage() + 1);
		},
		cellValueChanged(grid) {
			this.localRows = grid.api.getRenderedNodes().map((node) => ({ ...node.data }));
			this.updateSelectedRows(grid);
		},
		updateSelectedRows(grid) {
			const newRows = grid.api.getSelectedRows().map((row) => ({ ...row }));
			this.$emit('update:selectedRows', [...newRows]);
		},
		setTotalPageNum() {
			this.$set(this, 'totalPageNum', this.gridApi?.paginationGetTotalPages());
		},
		addRow() {
			const sampleRow = this.rows[0];
			delete sampleRow[INTERNAL_ID_KEY];
			const newEmptyRow = Object.keys(sampleRow).reduce((result, current) => ({ ...result, [current]: '' }), {});
			// newEmptyRow._uniq = Math.floor(Math.random() * 100000);
			this.localRows.push(newEmptyRow);
			this.setTotalPageNum();
		},
	},
	watch: {
		searchString: {
			handler: debounce(async function (newV) {
				if (this.isSearchOn && this.gridApi) {
					this.gridApi.setQuickFilter(newV);
					this.gotoPage(1);
					this.setTotalPageNum();
				}
			}, 300),
			immediate: true,
		},
		localPageSize: {
			handler(newV) {
				if (newV && this.gridApi) {
					this.gridApi.paginationSetPageSize(Number(newV));
					this.gotoPage(1);
					this.setTotalPageNum();
				}
			},
			immediate: true,
		},
	},
	mounted() {
		this.localRows = [...this.rows];
	},
	data() {
		const { isSelectionOn, fieldsEditable, isMultipleSelection } = this;

		const isFirstColumn = (params) => {
			var displayedColumns = params.columnApi.getAllDisplayedColumns();
			var thisIsFirstColumn = displayedColumns[0] === params.column;
			return thisIsFirstColumn;
		};

		return {
			gridApi: null,
			gridColumnApi: null,
			statusBar: null,
			searchString: '',
			currentPage: 1,
			totalPageNum: 1,
			gridOptions: {
				rowSelection: isSelectionOn ? (isMultipleSelection ? 'multiple' : 'single') : undefined,
				rowMultiSelectWithClick: isMultipleSelection ? 'multiple' : undefined,
				suppressRowClickSelection: true,
				suppressFieldDotNotation: true,
				suppressPaginationPanel: true,
				singleClickEdit: true,
				stopEditingWhenCellsLoseFocus: true,
				headerHeight: 40,
				rowHeight: 40,
				defaultColDef: {
					flex: 1,
					minWidth: 100,
					headerCheckboxSelection: isSelectionOn && isMultipleSelection ? isFirstColumn : undefined,
					checkboxSelection: isSelectionOn ? isFirstColumn : undefined,
					resizable: true,
					valueFormatter: ({ value }) => {
						if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
							return JSON.stringify(value);
						}
						return value;
					},
					cellRenderer: fieldsEditable ? 'EditableCellRenderer' : (params) => params.value,
				},
			},
			gridListeners: {
				'selection-changed': this.updateSelectedRows,
				cellValueChanged: this.cellValueChanged,
			},
			localPageSize: this.pageSize,
			localRows: [],
		};
	},
};
</script>

<style lang="scss">
@import '~ag-grid-community/dist/styles/ag-grid.css';
@import '~ag-grid-community/src/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin.scss';
/* We are not scoping style component because it fucks up the imports of '@include ag-theme-alpine'  */
/*  We mannually scope it with the '.ag-theme-alpine' class */
/*  Which is ennough as long as we don't use ot anywhere else in our code base */

.new-table-view {
	$border-color: #ebeef5;
	$border-radius: 4px;
	$status-bar-height: 44px;

	.ag-theme-alpine {
		width: 100%;
		height: calc(100% - #{$status-bar-height});

		@include ag-theme-alpine(
			(
				border-radius: $border-radius,
				border-color: $border-color,
				cell-horizontal-border: solid $border-color,
				header-height: 32px,
				row-height: 35px,
				cell-horizontal-padding: ag-derived(grid-size, $times: 1),
			)
		);

		.ag-header-cell {
			// or write CSS selectors to make customisations beyond what the parameters support
			border-right: 1px solid $border-color;
			background-color: ag-param(background-color) !important;
			font-weight: 300;
			font-size: 15px;
			color: #909399;
		}

		.ag-cell {
			// or write CSS selectors to make customisations beyond what the parameters support
			border-right: 1px solid $border-color;
			font-weight: 300;
		}
		.ag-row.ag-row-selected {
			background-color: #d9ecfd !important;
		}
		.ag-cell-focus {
			border-color: transparent !important;
		}
		.ag-cell-value {
			width: 100%; // in order to render cell
		}
		.ag-row-hover {
			background-color: transparent; // disable hover background change
		}
		.ag-root-wrapper.ag-layout-normal {
			border-top-left-radius: $border-radius;
			border-top-right-radius: $border-radius;
			border-bottom-right-radius: 0;
			border-bottom-left-radius: 0;
		}
		.ag-row-odd {
			background-color: transparent;
		}
		&.is-fields-editable .ag-cell {
			cursor: text;
		}
		&.is-fields-editable .ag-cell:hover::before {
			transition: 0.3s;
			opacity: 1;
		}
		&.is-fields-editable .ag-cell:not(:hover)::before {
			opacity: 0;
			transition: 0.2s;
		}

		.ag-cell-inline-editing {
			box-shadow: none;
			background-color: transparent;

			.ag-text-field-input {
				margin: 2px 0 -2px 0 !important;
				padding: 19px 4px;
				border-radius: 0 !important;
				border: 0 solid transparent !important;
			}

			&:first-of-type {
				padding-left: 44px;
				border-left: 1px solid $border-color !important;

				&::after {
					content: '';
					color: var(--ag-checkbox-unchecked-color, #999);
					position: absolute;
					top: 0;
					left: 0;
					height: 16px;
					width: 16px;
					border: 2px solid #ebeef5;
					pointer-events: none;
					margin: 10px 10px 0 13px;
					border-radius: 2px;
				}
			}
		}
	}

	.ag-checkbox-input-wrapper {
		input[type='checkbox'] {
			cursor: pointer;
		}
		&::before {
			content: '';
			position: absolute;
			top: -16px;
			left: 30px;
			height: 44px;
			width: 1px;
			background-color: $border-color;
		}
		&:not(.ag-checked)::after {
			color: #dcdfe5 !important;
		}
		&.ag-checked::after {
			color: #4ea1f2 !important;
		}
	}

	&.is-search-on {
		.app-search-container {
			padding: 4px;
			border-top-left-radius: $border-radius;
			border-top-left-radius: $border-radius;
			border: 1px solid $border-color;
			border-bottom: 0;

			.search-field {
				margin: 0 !important;

				.v-input__icon i {
					color: #303133 !important;
				}
				.v-input__slot {
					padding: 0 12px;
				}
			}
		}

		.ag-root-wrapper.ag-layout-normal {
			border-top-left-radius: 0;
			border-top-right-radius: 0;
		}
	}

	&:not(.is-multiple-selection) {
		.ag-header-row {
			.ag-header-cell:first-of-type {
				.ag-header-select-all.ag-hidden {
					display: block !important;
					pointer-events: none;

					.ag-checkbox-input-wrapper {
						&::after {
							content: '';
						}
					}
				}
			}
		}

		.ag-header-icon {
			margin: 0 24px 0 8px;
		}
	}
}
</style>
